<template>
  <div class="novel-editor-list">
    <div class="head">
      <div class="controller">
        <div
          class="folder"
          @click="backToFolderList()"
          @mouseenter="onFolderMouseEnter"
          @mouseleave="hideFolderTooltip"
        >
          <img src="@/assets/img/icon/folder_black.png" class="folder-icon" />
        </div>
        <div class="title ellipsis">
          {{ folder ? folder.name : $t("manuscriptPage.manuscriptList.allManuscripts") }}
        </div>
        <div class="menu">
          <template v-if="isEditMode || isDeleteMode || isMoveFolderMode">
            <span class="edit-end" @click="saveOrder">{{ $t("manuscriptPage.manuscriptList.editEnd") }}</span>
          </template>
          <template v-else>
            <CogIcon class="flex flex-center" @click="isShowMenu = !isShowMenu" />
          </template>
        </div>
      </div>

      <div v-if="isShowMenu" class="hover-menu-mask" @click="onClickCloseMenu">
        <div class="hover-menu">
          <div class="hover-menu-item" @click="onClickSortManuscript">
            {{ $t("manuscriptPage.manuscriptList.sortManuscript") }}
          </div>
          <div class="hover-menu-item" @click="onClickMoveManuscript">
            {{ $t("manuscriptPage.manuscriptList.moveFolder") }}
          </div>
          <div class="hover-menu-item" @click="onClickImportFile">
            {{ $t("manuscriptPage.manuscriptList.importManuscript") }}
          </div>
          <div class="hover-menu-item" @click="toggleProgressDisplayMode">
            {{
              $t("manuscriptPage.manuscriptList.changeProgressDisplay", {
                unit: isProgressPercentage
                  ? $t("manuscriptPage.manuscriptList.characters")
                  : $t("manuscriptPage.manuscriptList.percentage"),
              })
            }}
          </div>
          <div class="hover-menu-item" @click="onClickDeleteManuscript">
            {{ $t("manuscriptPage.manuscriptList.deleteManuscript") }}
          </div>
          <div class="hover-menu-item close">{{ $t("manuscriptPage.manuscriptList.closeMenu") }}</div>
        </div>
      </div>

      <div class="writing-progress">
        <span>{{ $t("manuscriptPage.manuscriptList.totalCharacters", { count: formattedTotalWordLength }) }}</span>
        <span v-if="!folder" class="remaining-progress">{{
          $t("manuscriptPage.manuscriptList.remaining", {
            count: remainingCharacterCount,
            percent: remainingProgress,
          })
        }}</span>
      </div>
    </div>
    <div
      ref="folderTooltip"
      v-show="isFree && isFolderTooltipVisible"
      class="folder-premium-tooltip"
      :style="{ top: folderTooltipPosition.top + 'px', left: folderTooltipPosition.left + 'px' }"
    >
      {{ $t("exportManuscriptDialog.premiumOnly") }}
    </div>
    <div class="list">
      <sortable-list
        class="list-container"
        :items.sync="manuscripts"
        keyAttr="key"
        :disabled="!isEditMode"
        :extra="isEditMode"
      >
        <template v-slot:default="{ item, extra }">
          <list-item
            icon="none"
            :label="item.title || $t('manuscriptPage.manuscriptList.untitled')"
            :folderName="folderName(item)"
            :isActive="selected && item.key === selected.key"
            :showHandle="extra"
            :item="item"
            :customClickFunction="() => select(item.key)"
            :needsDoCustomClickFunction="true"
            :showProgress="true"
            :progress="unitProgress(item.content)"
            :progressWidth="progressChipMinWidthValue(item.content)"
            :showContentWarning="item.contentLoaded && item.contentStatus && item.contentStatus !== 'PRESENT'"
            :isEditMode="isEditMode"
            :isManuscriptDeleteMode="isDeleteMode"
            :isManuscriptMoveFolderMode="isMoveFolderMode"
            @clickSetFolder="setFolder"
            @delete="deleteManuscript(item.key, item.title || $t('manuscriptPage.manuscriptList.untitled'))"
          ></list-item>
        </template>
      </sortable-list>
      <button v-if="isMobileDevice" class="sp-create-button" @click="createManuscript()">
        {{ $t("manuscriptPage.manuscriptList.newCreate") }}
      </button>
    </div>
    <div v-if="!isMobileDevice" class="button-container">
      <div class="button" @click="handleCreateClick">
        <div class="button-icon">
          <icon-plus />
        </div>
        <span>{{ $t("manuscriptPage.manuscriptList.newAdd") }}</span>
      </div>
      <div class="button" @click="handlePreviewClick">
        <div class="button-icon preview-icon">
          <icon-preview />
        </div>
        <span>{{ $t("manuscriptPage.manuscriptList.preview") }}</span>
      </div>
      <div class="button" @click="handleProperNounClick">
        <div class="button-icon">
          <icon-proper-noun />
        </div>
        <span>{{ $t("manuscriptPage.manuscriptList.properNoun") }}</span>
      </div>
      <div v-if="!isScriptMode" class="button" :class="{ active: isAssistPanelVisible }" @click="handleAssistClick">
        <div class="assist-icon">
          <img :src="assistIconSrc" :alt="$t('manuscriptPage.manuscriptList.assistIconAlt').toString()" />
        </div>
        <span>{{ $t("manuscriptPage.manuscriptList.assist") }}</span>
      </div>
    </div>

    <proper-noun-modal
      v-if="isProperNounModalOpen"
      :novelId="novelId"
      @close="isProperNounModalOpen = false"
    />

    <input style="display: none" ref="input" type="file" accept=".txt,.docx" multiple @change="onSelectFile()" />
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import CogIcon from "icons/Cog.vue";
import isMobile from "ismobilejs";
import { showOkCancelDialog } from "@/lib/dialog";
import ManuscriptDeleteConfirm, {
  ManuscriptDeleteConfirmProps,
} from "@/components/ui/novel/ManuscriptDeleteConfirm.vue";
import { Dialog } from "@/lib/utils";
import { Manuscript, ManuscriptFolder, Plan } from "@/lib/models";
import {
  filterSupportedManuscriptFiles,
  openManuscriptFilePicker,
  readManuscriptFiles,
  ManuscriptContentTooLongError,
} from "@/lib/utils/manuscriptFileImport";
import SimpleDialog, { SimpleDialogProps } from "@/components/ui/dialogs/SimpleDialog.vue";
import ListItem from "@/components/molecules/ListItem.vue";
import SortableList from "@/components/molecules/SortableList.vue";
import { ManuscriptClient } from "@/lib/clients";
import assistIconSrc from "@/assets/icons/assist-icon.svg";
import { logError } from "@/lib/utils/logger";
import ProperNounModal from "@/components/organisms/ProperNounModal.vue";
import IconPlus from "~icons/figma/Plus";
import IconPreview from "~icons/figma/Preview";
import IconProperNoun from "~icons/figma/ProperNoun";

const manuscriptClient = new ManuscriptClient();

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    CogIcon,
    ListItem,
    SortableList,
    ProperNounModal,
    IconPlus,
    IconPreview,
    IconProperNoun,
  },
  props: {
    novelId: String,
    selected: Object,
    items: Array as PropType<Manuscript[]>,
    folder: Object as PropType<ManuscriptFolder>,
    allFolders: Array as PropType<ManuscriptFolder[]>,
    wordLength: Number,
  },

  data() {
    return {
      manuscripts: this.items,
      isEditMode: false,
      isShowMenu: false,
      isDeleteMode: false,
      isMoveFolderMode: false,
      assistIconSrc,
      isFolderTooltipVisible: false,
      folderTooltipPosition: { top: 0, left: 0 },
      isProperNounModalOpen: false,
    };
  },

  watch: {
    items() {
      this.manuscripts = this.items;
    },
  },

  mounted() {
    const tooltip = this.$refs.folderTooltip as HTMLElement | undefined;
    if (tooltip) {
      document.body.appendChild(tooltip);
    }
  },

  beforeDestroy() {
    const tooltip = this.$refs.folderTooltip as HTMLElement | undefined;
    if (tooltip && tooltip.parentNode) {
      tooltip.parentNode.removeChild(tooltip);
    }
  },

  methods: {
    async select(manuscriptKey) {
      const { novelId } = this;

      if (this.isModified()) {
        const yes = await showOkCancelDialog({
          title: this.$t("manuscriptPage.manuscriptList.confirmDialog.title") as string,
          content: this.$t("manuscriptPage.manuscriptList.confirmDialog.content") as string,
          okButton: this.$t("manuscriptPage.manuscriptList.confirmDialog.okButton") as string,
          cancelButton: this.$t("manuscriptPage.manuscriptList.confirmDialog.cancelButton") as string,
        });
        if (!yes) {
          return;
        }
      }
      this.setModified(false);

      if (manuscriptKey) {
        this.$router.push(
          {
            name: "editor",
            params: {
              novelId,
              manuscriptKey,
            },
          },
          () => {}
        );
      } else {
        this.$router.push(
          {
            name: "editor",
            params: {
              novelId,
            },
          },
          () => {}
        );
      }

      this.$emit("select", manuscriptKey);
    },
    async createManuscript(title = "", content = "") {
      const { novelId, folder } = this;
      const manuscriptFolderId = folder ? folder.manuscriptFolderId : null;

      this.$store.dispatch("manuscriptModule/createManuscript", {
        novelId,
        title,
        content,
        manuscriptFolderId,
        callback: async () => {
          await this.updateManuscriptFolders();

          if (!this.isModified()) {
            this.select(this.createdManuscriptKey);
            this.$store.dispatch("manuscriptModule/initializeCreatedManuscriptKey");
          }

          this.updateOrder();
        },
      });
    },
    async createMultipleManuscript(payload) {
      const { novelId, folder } = this;
      const manuscriptFolderId = folder ? folder.manuscriptFolderId : null;

      const params = payload.map((item) => ({
        novelId,
        title: item.title || "",
        content: item.content || "",
        manuscriptFolderId,
      }));

      await this.$store.dispatch("manuscriptModule/createMultipleManuscript", {
        params,
        callback: async () => {
          await this.updateManuscriptFolders();
        },
      });
    },
    async deleteManuscript(key, title) {
      const { novelId, getEpisodeIdFromNolaNovel } = this;
      const episodeId = getEpisodeIdFromNolaNovel(key);
      const confirmDialog = new Dialog(ManuscriptDeleteConfirm);
      const data: ManuscriptDeleteConfirmProps = {
        title,
        novelId,
        manuscriptId: key,
        episodeId,
      };

      const result = await confirmDialog.show(data);

      if (result) {
        if (!this.isModified()) {
          this.select();
        }

        this.$notify({
          title: this.$t("manuscriptPage.manuscriptList.deleteNotification") as string,
          type: "error",
        });

        this.updateOrder();
        this.updateManuscriptFolders();
      }
    },
    backToFolderList() {
      this.$emit("backToFolderList");
    },
    setFolder(item) {
      this.$emit("clickSetFolder", {
        manuscript: item,
        manuscriptFolder: this.folder,
      });
    },
    previewAll() {
      this.$router.push({ name: "preview" });
    },
    emitCreated() {
      this.$emit("created");
    },

    getManuscript(id) {
      const { getters } = this.$store;
      return getters["manuscriptModule/manuscript"](id);
    },
    getEpisodeIdFromNolaNovel(manuscriptId) {
      const { getManuscript } = this;
      const manuscript = getManuscript(manuscriptId);
      if (!manuscript) {
        return undefined;
      }

      const { associatedData } = manuscript;

      if (!associatedData) {
        return undefined;
      }

      const { nolaNovel } = associatedData;

      if (!nolaNovel) {
        return undefined;
      }

      const { id } = nolaNovel;

      if (!id) {
        return undefined;
      }

      return id;
    },
    onClickImportFile() {
      openManuscriptFilePicker(this.$refs.input as HTMLInputElement);
    },
    async onSelectFile() {
      const fileList = (this.$refs.input as HTMLInputElement).files;

      if (fileList) {
        // サポートしているファイル形式（.txt / .docx）以外・Word 一時ファイルを除外
        const supportedFileList = filterSupportedManuscriptFiles(Array.from(fileList));

        // 対応形式のファイルが1件も無い場合は案内する（フォルダ内全選択などで発生しうる）
        if (supportedFileList.length === 0) {
          const noFileDialog = new Dialog(SimpleDialog);
          const noFileData: SimpleDialogProps = {
            title: this.$t("manuscriptPage.manuscriptList.importError.title") as string,
            content: this.$t("manuscriptPage.manuscriptList.importNoSupportedFileMessage") as string,
            isError: true,
            width: 350,
          };
          noFileDialog.show(noFileData);
          return;
        }

        try {
          this.$emit("changeLoading", true);

          const payloads = await readManuscriptFiles(supportedFileList, this.isScriptMode);

          await this.createMultipleManuscript(payloads);

          const completeDialog = new Dialog(SimpleDialog);
          const completeData: SimpleDialogProps = {
            title: this.$t("manuscriptPage.manuscriptList.importComplete.title") as string,
            content: this.$t("manuscriptPage.manuscriptList.importComplete.content") as string,
            width: 350,
          };
          completeDialog.show(completeData);
        } catch (error) {
          logError({ module: "ManuscriptList", action: "onSelectFile" }, "原稿のインポートに失敗", error);

          const errorDialog = new Dialog(SimpleDialog);
          const errorData: SimpleDialogProps =
            error instanceof ManuscriptContentTooLongError
              ? {
                  title: this.$t("manuscriptPage.manuscriptList.importContentTooLong.title") as string,
                  content: this.$t("manuscriptPage.manuscriptList.importContentTooLong.content", {
                    fileName: error.fileName,
                    maxLength: error.maxLength,
                  }) as string,
                  isError: true,
                  width: 350,
                }
              : {
                  title: this.$t("manuscriptPage.manuscriptList.importError.title") as string,
                  content: this.$t("manuscriptPage.manuscriptList.importError.content") as string,
                  isError: true,
                  width: 350,
                };
          errorDialog.show(errorData);
        } finally {
          this.$emit("changeLoading", false);
        }
      }
    },
    onClickSortManuscript() {
      this.isEditMode = true;
    },
    onClickCloseMenu() {
      this.isShowMenu = false;
    },
    toggleProgressDisplayMode() {
      this.onClickCloseMenu();
      this.$store.dispatch("manuscriptModule/toggleProgressPercentage");
    },
    async updateOrder() {
      if (this.folder) return; // フォルダ内の原稿の順番はフォルダの順番に従うため、ここでは更新しない

      if (this.manuscripts.length !== 0) {
        const order = this.manuscripts.map((manuscript: Manuscript) => manuscript.key);
        await this.$store.dispatch("manuscriptModule/updateOrder", { novelId: this.novelId, order });
      }
    },
    async saveOrder() {
      const { novelId, manuscripts } = this;
      const order = manuscripts.map((item) => item.key);

      if (this.folder) {
        const folders = this.allFolders;
        const manuscriptFolders = folders.map((folder) => {
          const item = folder;
          if (item.manuscriptFolderId === this.folder.manuscriptFolderId) {
            item.manuscriptKeys = order as string[];
          }
          return item;
        });

        await this.$store.dispatch("manuscriptModule/updateFolder", { novelId, manuscriptFolders });
      } else {
        this.updateOrder();
        this.$emit("changeLoading", true);
        this.$store.dispatch("manuscriptModule/initialize", {
          novelId,
          callback: () => {
            this.$emit("changeLoading", false);
          },
        });
      }

      this.isEditMode = false;
      this.isDeleteMode = false;
      this.isMoveFolderMode = false;
    },
    async updateManuscriptFolders() {
      const manuscriptFolders = await manuscriptClient.fetchAllManuscriptFolder(this.novelId);
      await this.$store.dispatch("manuscriptModule/updateFolder", { novelId: this.novelId, manuscriptFolders });
    },
    onClickDeleteManuscript() {
      this.isDeleteMode = true;
    },
    onClickMoveManuscript() {
      this.isMoveFolderMode = true;
    },
    handleCreateClick() {
      this.$emit("notAiFeedback");
      this.createManuscript();
    },
    handlePreviewClick() {
      this.$emit("notAiFeedback");
      this.previewAll();
    },
    handleAssistClick() {
      // アシストパネルの表示/非表示を切り替え
      if (this.isAssistPanelVisible) {
        this.$store.dispatch("assistNavigationModule/hidePanel");
      } else {
        this.$store.dispatch("assistNavigationModule/showPanel");
      }
    },
    handleProperNounClick() {
      this.isProperNounModalOpen = true;
    },
    onFolderMouseEnter(event: MouseEvent) {
      if (!this.isFree) return;
      const target = event.currentTarget as HTMLElement | null;
      if (!target) return;
      const rect = target.getBoundingClientRect();
      this.folderTooltipPosition = {
        top: rect.bottom + 4,
        left: rect.left,
      };
      this.isFolderTooltipVisible = true;
    },
    hideFolderTooltip() {
      this.isFolderTooltipVisible = false;
    },
  },

  computed: {
    totalWordLength() {
      let total: number = 0;
      if (this.items) {
        this.items.forEach((x) => {
          total += this.contentLength(x.content);
        });
      }
      return total;
    },
    formattedTotalWordLength() {
      return this.totalWordLength.toLocaleString(this.$i18n.locale || "ja");
    },
    remainingCharacterCount() {
      const { wordLength, totalWordLength } = this;
      if (!wordLength) return "--";

      const remaining = Math.max(wordLength - totalWordLength, 0);
      return remaining.toLocaleString(this.$i18n.locale || "ja");
    },
    remainingProgress() {
      const { wordLength, totalWordLength } = this;
      if (!wordLength) return "--";
      if (totalWordLength >= wordLength) return 0;

      return Math.trunc((1 - totalWordLength / wordLength) * 100);
    },
    unitProgress() {
      return (content) => {
        const { wordLength } = this;
        if (content && wordLength) {
          if (this.isProgressPercentage) {
            return `${Math.floor((this.contentLength(content) / wordLength) * 100)}%`;
          }

          return `${this.contentLength(content)}字`;
        }
        return "--";
      };
    },
    contentLength() {
      return (content?: string) => {
        if (!content) return 0;

        let rubyLength = 0;

        // eslint-disable-next-line no-control-regex
        const rubyRegExp = new RegExp("[|｜][^|｜\r\n]*?《.*?》", "gu");
        const textWithRuby = content.match(rubyRegExp);
        if (textWithRuby) {
          textWithRuby.forEach((text) => {
            const onlyRubyRegExp = new RegExp("《.*?》$");
            const textOnlyRuby = text.match(onlyRubyRegExp);
            rubyLength += textOnlyRuby![0].length + 1; // 親文字の縦棒をカウントから除くための+1;
          });
        }

        let contentLength = 0;
        switch (this.countType) {
          case "addBlank":
            contentLength = content.replace(/\n/g, "").length;
            break;
          case "addNewLine":
            contentLength = content.replace(/[^\S\n]/g, "").length;
            break;
          case "all":
            contentLength = content.length;
            break;
          case "onlyCharacter":
          default:
            contentLength = content.replace(/\s+|\n/g, "").length;
            break;
        }

        return contentLength - rubyLength;
      };
    },
    createdManuscriptKey() {
      return this.$store.getters["manuscriptModule/createdManuscriptKey"];
    },
    countType() {
      return this.$store.getters["manuscriptModule/countType"];
    },
    progressChipMinWidthValue() {
      return (content: string) => {
        const baseWidth = 40;
        const widthPerChar = 8;

        if (this.isProgressPercentage) {
          return `${baseWidth}px`; // パーセント表示の場合は固定値
        }
        // 文字数表示の場合
        const contentDigit = String(content.length).length;
        // 3桁以内ならbaseWidth、4桁以降は桁数ごとにwidthPerCharを追加
        const additionalWidth = contentDigit > 3 ? (contentDigit - 3) * widthPerChar : 0;
        return `${baseWidth + additionalWidth}px`;
      };
    },
    isProgressPercentage() {
      return this.$store.getters["manuscriptModule/progressPercentage"];
    },
    foldersBelongManuscript() {
      return (manuscriptKey: string) => this.$store.getters["manuscriptModule/foldersBelongManuscript"](manuscriptKey);
    },
    folderName() {
      return (item) => {
        if (item.key !== undefined) {
          const folders = this.foldersBelongManuscript(item.key);
          return folders.reduce((acc, cur) => (acc ? `${acc} / ${cur.name}` : `${cur.name}`), "");
        }
        return "";
      };
    },
    isMobileDevice() {
      return isMobile().phone || (isMobile().tablet && window.innerWidth < 1024);
    },
    isAssistPanelVisible() {
      return this.$store.getters["assistNavigationModule/isVisible"];
    },
    isScriptMode() {
      return this.$store.getters["novelModule/isScriptMode"](this.novelId);
    },
    isFree() {
      return this.$store.getters["billingModule/plan"] === Plan.free;
    },
  },
});

interface Props {
  novelId: string;
  selected: any;
  items: any[];
  folder: any;
  allFolders: ManuscriptFolder[];
  wordLength: number;
}

interface Data {
  manuscripts: Manuscript[];
  isEditMode: boolean;
  isShowMenu: boolean;
  isDeleteMode: boolean;
  isMoveFolderMode: boolean;
  assistIconSrc: string;
  isFolderTooltipVisible: boolean;
  folderTooltipPosition: { top: number; left: number };
  isProperNounModalOpen: boolean;
}

interface Methods {
  select: (manuscriptKey?: string) => Promise<void>;
  createManuscript: (title?: string, content?: string) => Promise<void>;
  createMultipleManuscript: (payload: { title?: string; content?: string }[]) => Promise<void>;
  deleteManuscript: (key: string, title: string) => Promise<void>;
  backToFolderList: () => void;
  setFolder: (item: object) => void;
  previewAll: () => void;
  emitCreated(): void;
  getManuscript(manuscriptId: string): Manuscript | undefined;
  getEpisodeIdFromNolaNovel(manuscriptId: string): string | undefined;
  onClickImportFile(): void;
  onSelectFile(): Promise<void>;
  onClickSortManuscript(): void;
  onClickCloseMenu(): void;
  toggleProgressDisplayMode(): void;
  updateOrder(): void;
  saveOrder(): void;
  updateManuscriptFolders(): void;
  onClickDeleteManuscript(): void;
  onClickMoveManuscript(): void;
  handleCreateClick(): void;
  handlePreviewClick(): void;
  handleAssistClick(): void;
  handleProperNounClick(): void;
  onFolderMouseEnter(event: MouseEvent): void;
  hideFolderTooltip(): void;
}

interface Computed {
  totalWordLength: number;
  formattedTotalWordLength: string;
  remainingCharacterCount: string;
  remainingProgress: number | string;
  unitProgress: (content: string) => number | string;
  contentLength: (content: string) => number;
  createdManuscriptKey: string;
  countType: string;
  progressChipMinWidthValue: (content: string) => string;
  isProgressPercentage: boolean;
  foldersBelongManuscript: (manuscriptKey: string) => ManuscriptFolder[];
  folderName: (item: Manuscript) => string;
  isMobileDevice: boolean;
  isAssistPanelVisible: boolean;
  isScriptMode: boolean;
  isFree: boolean;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";
@mixin link {
  background: transparent;
  border: none;
  cursor: pointer;
}

.novel-editor-list {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.head {
  z-index: 1;
  flex-shrink: 0;
  color: var(--color-text-normal);
  font-weight: var(--font-weight-bold);
  background-color: var(--color-surface-subtle);
  position: relative;

  .controller {
    display: flex;
    justify-content: space-between;

    .folder {
      position: relative;
      background-color: var(--color-surface-muted);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      transition: background-color 0.2s ease;

      @include hover-overlay;

      .folder-icon {
        width: 1rem;
        vertical-align: middle;
        margin: 10px;
        filter: brightness(0);
        transition: transform 0.2s ease;

        [data-app-theme="dark"] & {
          filter: brightness(0) invert(1);
        }
      }

      &:hover .folder-icon {
        transform: scale(1.1);
      }
    }

    .title {
      display: inline-block;
      align-items: center;
      flex-grow: 1;
      padding: 10px 15px;
      width: 100%;
    }

    .menu {
      display: flex;
      align-items: center;
      margin-right: 10px;
      cursor: pointer;

      svg {
        fill: var(--color-text-normal);
      }

      .edit-end {
        min-width: 55px;
        text-align: right;
      }
    }
  }

  .hover-menu-mask {
    position: absolute;
    top: 40px;
    background-color: var(--color-overlay);
    width: 100%;
    height: calc(100vh - 40px);

    .hover-menu {
      position: absolute;
      width: 100%;

      .hover-menu-item {
        position: relative;
        background-color: var(--color-background-normal);
        color: var(--color-text-normal);
        padding: 10px;
        border-bottom: 1px solid var(--color-border);
        cursor: pointer;

        &.close {
          padding: 5px;
          text-align: center;
          background-color: var(--color-surface-subtle);
        }

        @include hover-overlay;
      }
    }
  }

  .writing-progress {
    color: var(--color-text-normal);
    background-color: var(--color-surface-muted);
    padding: 8px 10px;
    font-size: var(--font-size-xs);
    display: flex;
    flex-wrap: wrap;
    overflow-wrap: break-word;

    .remaining-progress {
      min-width: 0;
    }
  }
}

.folder-premium-tooltip {
  position: fixed;
  z-index: 10;
  @include premium-tooltip;
}

.list {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .list-container {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }
}

.button-container {
  flex-shrink: 0;
  background-color: var(--color-surface-subtle);
  display: flex;
  justify-content: space-around;
  padding: 0;
  margin-top: auto;
  height: 50px;

  span {
    font-weight: var(--font-weight-bold);
    font-size: var(--font-size-xs);
  }

  .button {
    position: relative;
    border: none;
    background-color: var(--color-surface-subtle);
    color: var(--color-text-normal);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    flex: 1;
    padding: 10px 0;
    margin: 0;
    cursor: pointer;
    transition: background-color 0.3s;

    @include hover-overlay;

    &.active {
      background-color: var(--color-primary);
      color: var(--color-text-on-fixed-bg);

      .button-icon {
        color: var(--color-text-on-fixed-bg);
      }

      .assist-icon img {
        filter: brightness(0) saturate(100%) invert(100%);
      }
    }

    .button-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      margin-top: 5px;
      color: var(--color-text-normal);

      svg {
        width: 16px !important;
        height: 16px !important;
      }

      // Vue コンポーネントに対する直接スタイリング
      ::v-deep svg {
        width: 16px !important;
        height: 16px !important;
      }

      .figma-icon {
        width: 16px;
        height: 16px;
      }
    }

    .assist-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      margin-top: 5px;

      img {
        width: 16px;
        height: 16px;
        filter: brightness(0) saturate(100%) invert(23%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0%)
          contrast(100%);

        [data-app-theme="dark"] & {
          filter: brightness(0) invert(0.8);
        }
      }
    }
  }
}

.sp-create-button {
  position: relative;
  flex-shrink: 0;
  width: 100%;
  height: 50px;
  margin: 0;
  padding: 0;
  font-size: 1.2em;
  font-weight: var(--font-weight-bold);
  border: none;
  transition: filter 0.3s;
  color: var(--color-text-on-fixed-bg);
  background-color: var(--color-primary);

  @include hover-overlay;
}
</style>
